import { DashboardResultLine } from "./server-types/DashboardResultLine.js";
import { PlanForecastNotes } from "./server-types/MonthSalesReportPlanForecastNotes.js";
import { SalesReportPlan, SalesReportPlanResponse } from "./server-types/SalesReportPlan.js";
import { TeamMember } from "./server-types/TeamMember.js";

export class ServerInterface {

    API_ENDPOINT = "";
    JWT: string = "";

    constructor(endpoint: string, jwt: string) {
        this.API_ENDPOINT = endpoint;
        this.JWT = jwt;
    }

    dashboard(year: number, month: number): Promise<DashboardResultLine[]> {
        return this.get(`dashboard/${year}/${month}`) as Promise<DashboardResultLine[]>;
    }

    getMemberList(): Promise<TeamMember[]> {
        return this.get("team-members") as Promise<TeamMember[]>;
    }

    createPlan(planData: SalesReportPlan): Promise<SalesReportPlanResponse> {
        return this.post("plans", planData) as Promise<SalesReportPlanResponse>;
    }

    getPlan(planId: string): Promise<SalesReportPlanResponse> {
        return this.get(`plans/${planId}`) as Promise<SalesReportPlanResponse>;
    }

    updatePlan(planId: string, planData: { id: string; planName: string; months: number[]; }): Promise<SalesReportPlanResponse> {
        return this.put(`plans/${planId}`, planData) as Promise<SalesReportPlanResponse>;
    }

    deletePlan(planId: string): Promise<void> {
        return this.delete(`plans/${planId}`) as Promise<void>;
    }

    updatePlanMtd(planId: string, month: number, monthToDate: number): Promise<void> {
        return this.post(`plans/${planId}/${month}/mtd`, monthToDate) as Promise<void>;
    }

    getSelf(year: number, month: number): Promise<PlanForecastNotes[]> {
        return this.get(`self/${year}/${month}`) as Promise<PlanForecastNotes[]>;
    }

    setSelfForecast(planId: string, month: number, forecast: number) {
        return this.post(`self/${planId}/${month}/forecast`, forecast);
    }

    addSelfNote(planId: string, month: number, note: string) {
        return this.post(`self/${planId}/${month}/note`, note);
    }

    async get(endpoint: string): Promise<unknown> {
        let result = await fetch(this.API_ENDPOINT + endpoint, {
            method: "GET",
            headers: {
                "Authorization": "Bearer " + this.JWT
            }
        });

        if (result.ok) {
            return result.json().then(data => data, () => null);
        } else {
            alert("Error processing request!");
            throw new Error(`HTTP GET: ${this.API_ENDPOINT}${endpoint} ${result.status}\n${result.body}`);
        }
    }

    async post(endpoint: string, body: any): Promise<unknown> {
        const result = await fetch(this.API_ENDPOINT + endpoint, {
            method: "POST",
            headers: {
                "Authorization": "Bearer " + this.JWT,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
        });

        if (result.ok) {
            return result.json().then(data => data, () => null);
        } else {
            alert("Error processing request!");
            throw new Error(`HTTP POST: ${this.API_ENDPOINT}${endpoint} ${result.status}\n${result.body}`);
        }
    }

    async put(endpoint: string, body: any): Promise<unknown> {
        const result = await fetch(this.API_ENDPOINT + endpoint, {
            method: "PUT",
            headers: {
                "Authorization": "Bearer " + this.JWT,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
        });

        if (result.ok) {
            return result.json().then(data => data, () => null);
        } else {
            alert("Error processing request!");
            throw new Error(`HTTP PUT: ${this.API_ENDPOINT}${endpoint} ${result.status}\n${result.body}`);
        }
    }

    async delete(endpoint: string): Promise<unknown> {
        const result = await fetch(this.API_ENDPOINT + endpoint, {
            method: "DELETE",
            headers: {
                "Authorization": "Bearer " + this.JWT,
                "Content-Type": "application/json"
            }
        });

        if (result.ok) {
            return result.json().then(data => data, () => null);
        } else {
            alert("Error processing request!");
            throw new Error(`HTTP DELETE: ${this.API_ENDPOINT}${endpoint} ${result.status}\n${result.body}`);
        }
    }
}